import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";

const SERVIZI = [
  "Massaggio Olistico",
  "Trattamento 5 Elementi",
  "Riflessologia Plantare",
  "Massaggio Decontratturante",
  "Respiro Consapevole",
  "Yoga Dolce",
  "Meditazione Guidata",
  "Benessere Femminile",
  "Benessere Digestivo",
  "Laboratorio Cucina Consapevole",
  "Mindful Eating",
  "Consulenza / Altro",
];

const INFO = [
  {
    icon: Phone,
    title: "Telefono",
    lines: ["348 859 8613"],
    href: "tel:+393488598613",
    testid: "contact-phone",
  },
  {
    icon: Mail,
    title: "Email",
    lines: ["artemisia.agnese@gmail.com"],
    href: "mailto:artemisia.agnese@gmail.com",
    testid: "contact-email",
  },
  {
    icon: MapPin,
    title: "Lo Studio",
    lines: ["Via Stortina 1, 31023 Resana (TV)", "Su appuntamento · Lun–Ven 13:45–19:00 · Sab 10:30–16:00"],
    href: "https://www.google.com/maps/search/?api=1&query=Via+Stortina+1,+31023+Resana+TV",
    testid: "contact-studio",
  },
];

const Contatti = () => {
  const [params] = useSearchParams();
  const preset = params.get("servizio") || "";
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefono: "",
    servizio: preset,
    data_preferita: "",
    messaggio: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      toast.success("Richiesta inviata con cura. Ti ricontatterò presto.");
    } catch {
      toast.error("Qualcosa non ha funzionato. Riprova o scrivimi su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-3xl bg-cream-dark/60 px-6 py-4 text-sm text-ink placeholder:text-ink/35 outline-none transition-shadow duration-300 focus:shadow-[0_0_0_3px_rgba(79,100,91,0.18)]";

  return (
    <div data-testid="contatti-page" className="pt-40">
      <header className="mx-auto max-w-6xl px-6 text-center">
        <Reveal y={16}><Eyebrow className="text-center">Contatti &amp; Prenotazioni</Eyebrow></Reveal>
        <h1 className="mt-6 font-serif text-5xl leading-tight sm:text-6xl" data-testid="contatti-title">
          <MaskedLine delay={0.1} inView>Inizia il tuo</MaskedLine>
          <MaskedLine delay={0.22} inView><span className="italic text-sage">percorso</span></MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mx-auto mt-7 max-w-lg text-base leading-relaxed text-ink/65">
            Ti aspetto per ascoltare le tue necessità e trovare insieme il percorso
            giusto verso il tuo benessere interiore.
          </p>
        </Reveal>
        <Reveal delay={0.4}>
          <a
            href="https://wa.me/393488598613?text=Ciao%20Agnese%2C%20vorrei%20prenotare%20un%20trattamento"
            target="_blank"
            rel="noreferrer"
            data-testid="contatti-whatsapp-cta"
            className="mt-9 inline-flex items-center gap-3 rounded-full bg-[linear-gradient(135deg,#4F645B,#7A9488)] px-8 py-4 text-sm font-medium text-cream shadow-[0_12px_32px_rgba(79,100,91,0.25)] transition-transform duration-500 hover:scale-105"
          >
            <MessageCircle className="h-4 w-4" /> Prenota via WhatsApp
          </a>
        </Reveal>
      </header>

      <section className="mx-auto grid max-w-6xl gap-8 px-6 pt-20 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="space-y-6">
          {INFO.map((c, i) => (
            <Reveal key={c.title} delay={0.08 * i}>
              <div data-testid={c.testid} className="rounded-[2.5rem] bg-cream-soft p-9 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_48px_rgba(48,51,48,0.08)]">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sage-mist text-sage">
                  <c.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 font-serif text-2xl">{c.title}</h3>
                {c.href ? (
                  <a href={c.href} {...(c.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})} className="mt-2 block text-sm text-ink/60 transition-colors hover:text-sage-deep">
                    {c.lines.map((l) => <span key={l} className="block">{l}</span>)}
                  </a>
                ) : (
                  <p className="mt-2 text-sm text-ink/60">
                    {c.lines.map((l) => <span key={l} className="block">{l}</span>)}
                  </p>
                )}
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <div className="overflow-hidden rounded-[2.5rem]">
              <img src="/assets/massaggio-1.jpg" alt="Agnese durante un trattamento" className="aspect-[16/10] w-full object-cover" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="rounded-[3rem] bg-sage-mist/50 p-8 sm:p-12" data-testid="booking-form-panel">
            {sent ? (
              <div className="flex min-h-[28rem] flex-col items-center justify-center text-center" data-testid="booking-success">
                <CheckCircle2 className="h-14 w-14 text-sage" />
                <h2 className="mt-7 font-serif text-3xl italic">Grazie, {form.nome.split(" ")[0]}.</h2>
                <p className="mt-4 max-w-sm text-sm leading-relaxed text-ink/60">
                  La tua richiesta è arrivata. Ti ricontatterò al più presto per
                  confermare insieme data e dettagli.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ nome: "", email: "", telefono: "", servizio: "", data_preferita: "", messaggio: "" }); }}
                  data-testid="booking-another"
                  className="mt-9 rounded-full bg-sage px-7 py-3.5 text-sm font-medium text-cream transition-transform duration-500 hover:scale-105"
                >
                  Invia un'altra richiesta
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="booking-form">
                <h2 className="font-serif text-2xl italic">Richiedi la tua sessione</h2>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="nome" className="mb-2.5 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Nome e cognome</label>
                    <input id="nome" data-testid="booking-nome" required value={form.nome} onChange={set("nome")} placeholder="Il tuo nome" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2.5 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Email</label>
                    <input id="email" type="email" data-testid="booking-email" required value={form.email} onChange={set("email")} placeholder="tu@esempio.it" className={inputCls} />
                  </div>
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="telefono" className="mb-2.5 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Telefono</label>
                    <input id="telefono" data-testid="booking-telefono" value={form.telefono} onChange={set("telefono")} placeholder="348 000 0000" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="data" className="mb-2.5 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Data preferita</label>
                    <input id="data" type="date" data-testid="booking-data" value={form.data_preferita} onChange={set("data_preferita")} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="servizio" className="mb-2.5 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Servizio o evento</label>
                  <select id="servizio" data-testid="booking-servizio" required value={form.servizio} onChange={set("servizio")} className={`${inputCls} appearance-none`}>
                    <option value="" disabled>Scegli un servizio…</option>
                    {preset && !SERVIZI.includes(preset) && <option value={preset}>{preset}</option>}
                    {SERVIZI.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="messaggio" className="mb-2.5 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">Messaggio</label>
                  <textarea id="messaggio" data-testid="booking-messaggio" rows={4} value={form.messaggio} onChange={set("messaggio")} placeholder="Raccontami di cosa hai bisogno…" className={`${inputCls} resize-none`} />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="booking-submit"
                  className="flex w-full items-center justify-center gap-3 rounded-full bg-[linear-gradient(135deg,#4F645B,#7A9488)] px-8 py-4.5 py-4 text-sm font-semibold text-cream shadow-[0_12px_32px_rgba(79,100,91,0.25)] transition-all duration-500 hover:scale-[1.02] disabled:opacity-60"
                >
                  {sending ? (
                    <span className="animate-zen block h-4 w-4 rounded-full bg-cream" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {sending ? "Invio in corso…" : "Invia la richiesta"}
                </button>
                <p className="text-center text-xs text-ink/40">
                  Nessun pagamento ora: confermeremo insieme ogni dettaglio.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Contatti;
